import React from "react";
import { QueryStatus } from "@hex-insights/app-modules";
import { Button, Column, Conditional, Else, If, Modal, Row } from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { AttendanceRecordCountStatistics, StudentFilterInput } from "../../../../../../../../Utilities";
import { AttendanceStatusFormat } from "../../../../../../../Format";
import * as AttendanceStatusComponents from "../../../../../../AttendanceStatus";
import * as StudentComponents from "../../../../../../Student";
import styles from "../general.module.css";

const tableColumns = ["attendanceStatus", "count", "students"];

export type AttendanceRecordStatisticsTableProps = {
	date: string;
	queryStatus: QueryStatus;
	statistics: Pick<AttendanceRecordCountStatistics, "statuses" | "notTaken"> | undefined;
	studentFilters: StudentFilterInput | undefined;
};

export function AttendanceRecordStatisticsTable({
	date,
	queryStatus,
	statistics,
	studentFilters,
}: AttendanceRecordStatisticsTableProps) {
	return (
		<Table columns={tableColumns} className={styles["table"]}>
			<Table.Header>
				<Table.Row>
					<Table.Heading column="attendanceStatus" noResize className={styles["table__heading"]}>
						Attendance Status
					</Table.Heading>
					<Table.Heading column="count" noResize className={styles["table__heading"]}>
						No. Students
					</Table.Heading>
					<Table.Heading column="students" noResize className={styles["table__heading"]} style={{ textAlign: "right" }}>
						Students
					</Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				<Conditional>
					<If condition={queryStatus === "results"}>
						{statistics?.statuses.map((status) => (
							<TableRow
								key={status.id}
								status={status}
								count={status.count}
								date={date}
								studentFilters={studentFilters}
							/>
						))}
						<If condition={(statistics?.notTaken ?? 0) > 0}>
							<TableRow status={null} date={date} count={statistics?.notTaken ?? 0} studentFilters={studentFilters} />
						</If>
					</If>
					<Else>
						<Table.Row>
							<Table.Cell column={tableColumns[0]} colspan={tableColumns.length} className={styles["table__cell"]}>
								<Conditional>
									<If condition={queryStatus === "no-results"}>No results found.</If>
									<If condition={queryStatus === "error"}>Something went wrong</If>
									<If condition={queryStatus === "loading"}>Loading...</If>
								</Conditional>
							</Table.Cell>
						</Table.Row>
					</Else>
				</Conditional>
			</Table.Body>
		</Table>
	);
}

type TableRowProps = {
	date: string;
	status: AttendanceRecordCountStatistics["statuses"][0] | null;
	count: number;
	studentFilters?: StudentFilterInput;
};

function TableRow({ date, status, count, studentFilters: baseStudentFilters }: TableRowProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const studentFilters = React.useMemo<StudentFilterInput>(() => {
		let statusFilter: StudentFilterInput = {};
		if (!status) {
			statusFilter = { not: { schoolAttendanceRecords: [{ attendanceDateEQ: date }] } };
		} else {
			statusFilter = { schoolAttendanceRecords: [{ attendanceDateEQ: date, attendanceStatusIDEQ: status.id }] };
		}

		if (baseStudentFilters) {
			return { and: [baseStudentFilters, statusFilter] };
		}
		return statusFilter;
	}, [status, date, baseStudentFilters]);

	return (
		<React.Fragment>
			<Table.Row>
				<Table.Cell column="attendanceStatus" oneLine className={styles["table__cell"]} style={{ padding: 0 }}>
					<Row justify="spaced-start" align="center" style={{ height: "100%" }}>
						<AttendanceStatusComponents.AttendanceStatusBadge
							attendanceStatus={status}
							className={styles["table__badge"]}
						/>

						{status ? AttendanceStatusFormat.name(status) : "Not Taken"}
					</Row>
				</Table.Cell>
				<Table.Cell column="count" oneLine className={styles["table__cell"]}>
					{count}
				</Table.Cell>
				<Table.Cell column="students" oneLine className={styles["table__cell"]} style={{ padding: 0 }}>
					<Column justify="center" align="flex-end" style={{ height: "100%" }}>
						<Button variant="link" size="small" onClick={openModal}>
							View
						</Button>
					</Column>
				</Table.Cell>
			</Table.Row>

			<Modal.If condition={isModalOpen}>
				<StudentComponents.ControlledStudentsListModal filters={studentFilters} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
